import React from 'react'

import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Button,
  Alert,
} from '@mui/material'
import {
  IconTestPipe,
  IconSparkles,
  IconBrowser,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react'

interface TestGuidanceProps {
  propertyDefinition: {
    name: string
    aiManaged: boolean
    useWeb: boolean
  }
  onStartTest: () => void
  hasExistingTests?: boolean
  isFirstTime?: boolean
}

const TestGuidance: React.FC<TestGuidanceProps> = ({
  propertyDefinition,
  onStartTest,
  hasExistingTests = false,
  isFirstTime = true,
}) => {
  const features = []
  if (propertyDefinition.aiManaged) {
    features.push({ icon: IconSparkles, label: 'AI Analysis' })
  }
  if (propertyDefinition.useWeb) {
    features.push({ icon: IconBrowser, label: 'Web Research' })
  }

  return (
    <Stack
      spacing={2}
      sx={{ mb: 2 }}
    >
      {isFirstTime && (
        <Alert
          severity="info"
          icon={<IconAlertCircle size={20} />}
          sx={{
            '& .MuiAlert-message': {
              fontSize: '13px',
              lineHeight: '20px',
            },
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            gutterBottom
          >
            Test Your Property First
          </Typography>
          <Typography variant="body2">
            Before running a full backfill, test &ldquo;
            {propertyDefinition.name}&rdquo; on a few records to ensure it works
            as expected. This helps you refine the definition if needed.
          </Typography>
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ mb: 0.5 }}
            >
              Test Your Property
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Select 3-5 representative records to validate your property
              definition
            </Typography>
          </Box>

          {features.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {features.map((feature, index) => (
                <Chip
                  key={index}
                  icon={<feature.icon size={14} />}
                  label={feature.label}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '11px' }}
                />
              ))}
            </Box>
          )}

          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ mb: 1 }}
            >
              Recommended Test Approach:
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <IconCheck
                  size={16}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontSize: '12px' }}
                >
                  Choose records with different characteristics
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <IconCheck
                  size={16}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontSize: '12px' }}
                >
                  Include both simple and complex cases
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <IconCheck
                  size={16}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontSize: '12px' }}
                >
                  Review results before running full backfill
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Button
            variant="contained"
            startIcon={<IconTestPipe size={16} />}
            onClick={onStartTest}
            fullWidth
            sx={{ mt: 1 }}
          >
            Create Test
          </Button>
        </Stack>
      </Paper>

      {hasExistingTests && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'center' }}
        >
          You can run additional tests or proceed to backfill if satisfied with
          results
        </Typography>
      )}
    </Stack>
  )
}

export default TestGuidance
